export const COLOR_SCHEMES = ['light', 'dark'] as const;

export type ColorScheme = (typeof COLOR_SCHEMES)[number];

export const COLOR_SCHEME_PREFERENCES = ['system', ...COLOR_SCHEMES] as const;

export type ColorSchemePreference = (typeof COLOR_SCHEME_PREFERENCES)[number];

export const DEFAULT_COLOR_SCHEME_PREFERENCE: ColorSchemePreference = 'dark';

export const COLOR_SCHEME_STORAGE_KEY = 'launcher:color-scheme';

export const DARK_SCHEME_QUERY = '(prefers-color-scheme: dark)';

const OPPOSITE: Readonly<Record<ColorScheme, ColorScheme>> = {
  light: 'dark',
  dark: 'light',
};

type PreferenceStorage = Pick<Storage, 'getItem' | 'setItem'>;

export type StorageAccessor = () => PreferenceStorage | null | undefined;

export const isColorSchemePreference = (
  value: unknown,
): value is ColorSchemePreference =>
  COLOR_SCHEME_PREFERENCES.includes(value as ColorSchemePreference);

export const resolveColorScheme = (
  preference: ColorSchemePreference,
  systemDark: boolean,
): ColorScheme => {
  if (preference !== 'system') return preference;
  return systemDark ? 'dark' : 'light';
};

export const oppositeColorScheme = (scheme: ColorScheme): ColorScheme =>
  OPPOSITE[scheme];

export const readColorSchemePreference = (
  storage: StorageAccessor,
): ColorSchemePreference => {
  try {
    const stored = storage()?.getItem(COLOR_SCHEME_STORAGE_KEY);
    return isColorSchemePreference(stored)
      ? stored
      : DEFAULT_COLOR_SCHEME_PREFERENCE;
  } catch {
    return DEFAULT_COLOR_SCHEME_PREFERENCE;
  }
};

export const writeColorSchemePreference = (
  storage: StorageAccessor,
  preference: ColorSchemePreference,
): boolean => {
  try {
    const target = storage();
    if (!target) return false;
    target.setItem(COLOR_SCHEME_STORAGE_KEY, preference);
    return true;
  } catch {
    return false;
  }
};

export const applyColorScheme = (root: HTMLElement, scheme: ColorScheme) => {
  root.dataset.colorScheme = scheme;
  root.style.setProperty('color-scheme', scheme);
};

export const systemPrefersDark = (win: Pick<Window, 'matchMedia'>): boolean => {
  try {
    return typeof win.matchMedia === 'function'
      ? win.matchMedia(DARK_SCHEME_QUERY).matches
      : false;
  } catch {
    return false;
  }
};

export const browserStorage =
  (win: Window): StorageAccessor =>
  () =>
    win.localStorage;

export const bootColorScheme = (win: Window): ColorScheme => {
  const scheme = resolveColorScheme(
    readColorSchemePreference(browserStorage(win)),
    systemPrefersDark(win),
  );
  applyColorScheme(win.document.documentElement, scheme);
  return scheme;
};
